import { useState } from 'react'
import { Button, Input } from '@/components/ui'

export function ConfirmDeleteDialog({ title, message, onConfirm, onCancel, confirmPhrase = 'DELETE' }: {
  title: string
  message: string
  onConfirm: () => Promise<void>
  onCancel: () => void
  /** What has to be typed. Defaults to DELETE, which is fine when the
   * dialog names one thing you are looking at.
   *
   * Pass something specific when the action reaches further than the
   * screen suggests (#2194): typing DELETE is muscle memory from every
   * other product and can be done without reading, whereas typing an
   * application's name cannot be done absently and proves the person
   * knows WHICH thing they are emptying. */
  confirmPhrase?: string
}) {
  const [confirmText, setConfirmText] = useState('')
  const [failed, setFailed] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-lg shadow-lg p-6 w-96 max-w-[90vw] border">
        <h3 className="text-lg font-semibold text-destructive mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4">{message}</p>
        <p className="text-sm mb-2">
          Type <code className="px-1.5 py-0.5 bg-muted rounded font-mono">{confirmPhrase}</code> to confirm:
        </p>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={`Type ${confirmPhrase}`}
          className="mb-4"
          autoFocus
        />
        {/* A failed delete used to reject out of this handler with nothing
            catching it: an unhandled rejection, the dialog closed anyway,
            and no sign to the operator that the thing they confirmed did
            not happen (Copilot review). It stays open and says so. */}
        {failed && (
          <p className="text-sm text-destructive mb-3" role="alert">{failed}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={deleting}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={confirmText !== confirmPhrase || deleting}
            onClick={async () => {
              setDeleting(true)
              setFailed(null)
              try {
                await onConfirm()
                onCancel()
              } catch (err) {
                setFailed(
                  err instanceof Error
                    ? `Could not delete: ${err.message}`
                    : 'Could not delete. Try again.',
                )
              } finally {
                setDeleting(false)
              }
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  )
}
